import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges } from '../../utils/deals';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { useDeals } from '../../context/DealsContext';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('vp', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const cls = all.filter(d => d.status === 'Closed');
  const onh = all.filter(d => d.status === 'On Hold');
  const can = all.filter(d => d.status === 'Cancelled');
  const scs = new Set(ip.map(d => d.sc)).size;
  const pipelineOppGnarr = sumUniqueBy(all, d => d.opp_gnarr);
  const ages = validAges(all);
  const titleLbl = state.sub && state.sub !== 'All' ? state.sub : state.r;
  const crumb = state.sub && state.sub !== 'All';

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
    cancelled: number;
  }> = {};
  all.forEach(d => {
    const key = showSub ? d.sub_region : d.region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: d.region
    };
    regMap[key].book++;
    if (d.status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += d.gnarr;
    } else if (d.status === 'Closed') regMap[key].closed++;else if (d.status === 'On Hold') regMap[key].onhold++;else if (d.status === 'Cancelled') regMap[key].cancelled++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const ALLOWED_REGIONS = new Set(['AMER', 'EMEA', 'JAPAC', '']);
  const regRows = Object.entries(regMap).filter(([reg]) => showSub || ALLOWED_REGIONS.has(reg)).sort((a, b) => b[1].ip - a[1].ip);

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(d => {
    if (!cqMap[d.close_q]) cqMap[d.close_q] = {
      n: 0,
      g: 0
    };
    cqMap[d.close_q].n++;
    cqMap[d.close_q].g += d.gnarr;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-382">
      {/* Metrics */}
      <div className="mrow" style={{
      gridTemplateColumns: 'repeat(5, 1fr)'
    }} data-id="element-383">
       <MetricCard label="Total requests" value={ip.length + cls.length} sub={`Closed + In progress `} colorClass="c-green" data-id="element-384" />
       <MetricCard label="Closed requests" value={cls.length} colorClass="c-green" data-id="element-385" />
        <MetricCard label="In-progress requests" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-386" />
        <MedianAgeCard ages={ages} data-id="element-387" />
        <MetricCard label="GNARR Influenced" value={fmtG(pipelineOppGnarr)} sub={`Closed & In-progress requests`} colorClass="c-green" data-id="element-388" />
      </div>

      {/* Trend chart */}
      <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-390">{state.sub}</span>}</>} data-id="element-389" />
      <TrendChart region={state.r} sub={state.sub} status={state.status} deals={deals} data-id="element-391" />

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-393">{state.sub}</span>}</>} data-id="element-392" />
      <div className="tw" data-id="element-394">
        <div className="sub" data-id="element-395">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <table className="tbl" data-id="element-396">
          <thead data-id="element-397">
            <tr data-id="element-398">
              <th style={{
              width: '14%'
            }} data-id="element-399">Region</th>
              <th style={{
              width: '26%'
            }} data-id="element-400">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-401">Pipeline GNARR</th>
              <th style={{
              width: '12%'
            }} data-id="element-402">Closed</th>
              <th style={{
              width: '12%'
            }} data-id="element-403">Total</th>
              <th style={{
              width: '10%'
            }} data-id="element-404">On hold</th>
              <th style={{
              width: '10%'
            }} data-id="element-405">Sub-region</th>
            </tr>
          </thead>
          <tbody data-id="element-406">
            {regRows.map(([reg, d]) => <tr key={reg} onClick={() => onDrillRegion(d.parent || reg, showSub ? reg : '')} data-id="element-407">
                <td data-id="element-408">
                  <span className="rdot" style={{
                background: RDOTS[reg] || '#888'
              }} data-id="element-409" />
                  {reg || 'No DR'}
                </td>
                <td data-id="element-410">
                  <div className="pbg" style={{
                width: 90
              }} data-id="element-411">
                    <div className="pf" style={{
                  width: `${Math.round(d.ip / maxIP * 100)}%`
                }} data-id="element-412" />
                  </div>
                  <strong data-id="element-413">{d.ip}</strong>
                </td>
                <td data-id="element-414">{fmtG(d.gnarr)}</td>
                <td style={{
              color: d.closed > 0 ? '#15803D' : '#344054',
              fontWeight: d.closed > 0 ? 600 : 400
            }} data-id="element-415">{d.closed}</td>
                <td data-id="element-416">{d.book}</td>
                <td data-id="element-417">{d.onhold}</td>
                <td data-id="element-418">{showSub ? <span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-419">{d.parent}</span> : '—'}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Book status + CQ grid */}
      {/* <div className="br2">
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 8px' }}>
            <div className="shb" />
            <span className="sht">Book status mix</span>
          </div>
          <div className="book-bar">
            {segs.map(s => (
              <div key={s.l} style={{ width: `${Math.round(s.v / total * 100)}%`, height: '100%', background: s.c }} />
            ))}
          </div>
          <div className="bleg">
            {segs.map(s => (
              <span key={s.l}>
                <span className="bldot" style={{ background: s.c }} />
                {s.l} {s.v}
              </span>
            ))}
          </div>
        </div>
        <div className="bcard">
          <div className="sh" style={{ padding: '0 0 6px' }}>
            <div className="shb" />
            <span className="sht">In progress by close quarter</span>
          </div>
          <div className="cqg">
            {cqShow.map((q, i) => (
              <div key={q} className="cqc">
                <div className="cql">{q}</div>
                <div className="cqn">{cqMap[q].n}</div>
                <div className="cqs">in-progress deals</div>
                <div className="cqs">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{ background: qColors[i] || '#4f9cf9' }} />
              </div>
            ))}
          </div>
        </div>
       </div> */}

    </div>;
}